import type { StoreApi, UseBoundStore } from "zustand";
import { type IBracketStoreSlice, isIBracketStoreSlice, } from "./types";

export const awaitTruthy = async <T extends Record<string, unknown>>(store: UseBoundStore<StoreApi<T>>, selector: (state: T) => unknown, timeout: number = 100_000): Promise<void> => {
	return new Promise((resolve, reject) => {
		if(selector(store.getState())) {
			resolve();
			return;
		}

		const to = setTimeout(() => {
			unsubscribe();
			console.error(`Condition did not become truthy within ${timeout}ms`);
			console.error(selector);
			reject(new Error(`Condition did not become truthy within ${timeout}ms`));
		}, timeout);

		// Subscribe to store changes
		const unsubscribe = store.subscribe((state) => {
			if (selector(state)) {
				clearTimeout(to);
				unsubscribe();
				resolve();
			}
		});
	});
};




/**
 * Attempts to fetch a specified store slice after a timeout, while waiting for a provided condition to become truthy.<br>
 * Throws errors if the requested slice or its fetch method are not found on the store object.
 *
 * @template T - The state shape of the store object, which must extend Record<string, unknown>
 * @param {Object} params
 * @param {UseBoundStore<StoreApi<T>>} params.store - The zustand store instance to operate upon
 * @param {keyof UseBoundStore<StoreApi<T>>} params.slice - The property key for the desired slice of the store
 * @param {(state: T) => unknown} params.selector - A function that tests if a certain state is ready/truthy
 * @param {number} [params.timeout=0] - The delay in ms before attempting to run fetch on the slice
 * @returns {Promise<void>} A promise that resolves when the fetch is triggered and the selector has eventually become truthy,
 * or rejects if error conditions are encountered.
 */
export const loadAtTimeout = async ( sliceKey: string, timeout: number = 0 ): Promise<void> => {
	
	const slice = await getStoreSlice(sliceKey);
	if(slice.ready) {
		return;
	}
	if(timeout <= 0) {
		slice.fetch();
		return;
	}

	return new Promise((resolve, reject) => {
		setTimeout(async () => {
			try {
				slice.fetch();
				resolve();
			}
			catch (err) {
				reject(err);
			}
		}, timeout);
	});

};



export const getStoreSlice = async (slice: string): Promise<IBracketStoreSlice> => {
	const { useStore:store, } = await import("@store/useStore");
	const parts = slice.split(".");
	// @ts-ignore... 
	const sliced = parts.reduce((acc, part) => acc[part], store.getState());
	if(isIBracketStoreSlice(sliced)) {
		return sliced;
	}
	throw new Error(`Store slice ${String(slice)} does not have a ready property`);
};


export declare type ProfileProps = {
	info?: string;
}

export const profile = ( caller: string, props: ProfileProps = { info: "none"} ) => {
	if(!globalThis.location.search.includes("profile-store=true")) return;

	const call = {
		subject: caller,
		time: Date.now(),
		props,
	}
	console.log(`${caller} - ${props.info}`, call);
};
